import type { OutreachDraft } from '@discovr/types';
import { canonicalId, zonedDateTimeToISO } from '../../../../lib/timeZones';
import type { FirstTouchBlocker } from '../RowActions';
import { SKIP_LABELS } from '../outreachSendCopy';
import type { TemplateStepDraft } from '../templateValidation';
import type { WizardRun } from '../useSendWizardSession';

export type WizardStep = 'creators' | 'template' | 'drafts' | 'send';
export const WIZARD_STEPS: readonly WizardStep[] = ['creators', 'template', 'drafts', 'send'];
export type CreatorFilter = 'all' | 'has_email' | 'no_email';

/** `blocker` undefined ⇒ the target was built without a roster row (import path); eligibility falls back to `hasContact`. */
export interface WizardTarget {
  entryId: string;
  username: string;
  displayName?: string;
  avatarUrl?: string;
  email?: string;
  hasContact: boolean;
  sendTz?: string;
  blocker?: FirstTouchBlocker | null;
  /** false when Go says the campaign has no contact lookup. */
  lookupSupported?: boolean;
  /** Not read from a live row: off the current page, so the client cannot vouch for it and the server decides at send. */
  unverified?: boolean;
}
export interface WizardState {
  step: WizardStep; targets: WizardTarget[]; selected: string[]; filter: CreatorFilter; search: string;
  /** This batch's template (no lock): picked on the template step, frozen by Go when drafting starts. */
  templateId: number | null;
  /** The template step's working copy of the picked template's steps; null until they are loaded. */
  templateSteps: TemplateStepDraft[] | null;
  when: 'now' | 'later'; date: string; time: string; zone: string | null;
}
export type WizardAction =
  | { type: 'go'; step: WizardStep } | { type: 'next' } | { type: 'back' }
  | { type: 'toggle'; entryId: string } | { type: 'selectVisible'; entryIds: string[] } | { type: 'filter'; filter: CreatorFilter } | { type: 'search'; q: string }
  | { type: 'template'; templateId: number | null } | { type: 'templateSteps'; steps: TemplateStepDraft[] }
  | { type: 'when'; when: 'now' | 'later' } | { type: 'date'; date: string } | { type: 'time'; time: string } | { type: 'zone'; zone: string | null };

export const BLOCKER_LABELS: Record<FirstTouchBlocker, string> = {
  no_contact: SKIP_LABELS.no_contact,
  already_contacted: SKIP_LABELS.already_contacted,
  in_flight: 'First touch already queued',
  suppressed: SKIP_LABELS.suppressed,
};

/** A creator without an email is not blocked: the draft waits for one (include_no_email) and is reported on send. */
export function targetBlocker(target: WizardTarget): FirstTouchBlocker | null {
  if (target.unverified) return null;
  return target.blocker === undefined || target.blocker === 'no_contact' ? null : target.blocker;
}

/** The bulk bar's disabled reason, naming only the blockers the selection actually has. */
export function nothingSendableReason(targets: readonly WizardTarget[]): string | undefined {
  if (targets.length === 0) return undefined;
  const blockers = targets.map(targetBlocker);
  if (blockers.some((blocker) => blocker === null)) return undefined;
  const labels = [...new Set(blockers)].map((blocker) => BLOCKER_LABELS[blocker as FirstTouchBlocker].toLowerCase());
  return `No one selected can get a first touch — ${labels.join(', ')}`;
}

/** Short caption for the bulk bar when some, but not all, of the selection can get a first touch. */
export function partialSendNote(targets: readonly WizardTarget[]): string | undefined {
  const blocked = targets.map(targetBlocker).filter((blocker): blocker is FirstTouchBlocker => blocker !== null);
  if (blocked.length === 0 || blocked.length === targets.length) return undefined;
  const labels = [...new Set(blocked)].map((blocker) => BLOCKER_LABELS[blocker].toLowerCase());
  return `${blocked.length} of ${targets.length} can't get a first touch — ${labels.join(', ')}`;
}

export function initialWizardState(targets: WizardTarget[], defaults: { templateId: number | null; step?: WizardStep }): WizardState {
  return { step: defaults.step ?? 'creators', targets, selected: targets.map((target) => target.entryId), filter: 'all', search: '', templateId: defaults.templateId, templateSteps: null, when: 'now', date: '', time: '', zone: null };
}

export function wizardReducer(state: WizardState, action: WizardAction): WizardState {
  if (action.type === 'go') return { ...state, step: action.step };
  if (action.type === 'next' || action.type === 'back') {
    const index = WIZARD_STEPS.indexOf(state.step);
    const clamped = Math.max(0, Math.min(WIZARD_STEPS.length - 1, index + (action.type === 'next' ? 1 : -1)));
    return { ...state, step: WIZARD_STEPS[clamped] ?? state.step };
  }
  if (action.type === 'toggle') return { ...state, selected: state.selected.includes(action.entryId) ? state.selected.filter((id) => id !== action.entryId) : [...state.selected, action.entryId] };
  if (action.type === 'selectVisible') return { ...state, selected: [...new Set([...state.selected, ...action.entryIds])] };
  if (action.type === 'filter') return { ...state, filter: action.filter };
  if (action.type === 'search') return { ...state, search: action.q };
  if (action.type === 'template') return action.templateId === state.templateId ? state : { ...state, templateId: action.templateId, templateSteps: null };
  if (action.type === 'templateSteps') return { ...state, templateSteps: action.steps };
  if (action.type === 'when') return { ...state, when: action.when };
  if (action.type === 'date') return { ...state, date: action.date };
  if (action.type === 'time') return { ...state, time: action.time };
  return { ...state, zone: action.zone };
}

export function visibleTargets(state: WizardState): WizardTarget[] {
  const query = state.search.trim().toLowerCase();
  return state.targets.filter((target) => {
    // An unverified target's contact state is unknown, so only "all" lists it.
    const matchesFilter = state.filter === 'all' || (!target.unverified && (state.filter === 'has_email' ? target.hasContact : !target.hasContact));
    return matchesFilter && (!query || `${target.displayName ?? ''} ${target.username} ${target.email ?? ''}`.toLowerCase().includes(query));
  });
}
export function sendableTargets(state: WizardState): WizardTarget[] { return state.targets.filter((target) => state.selected.includes(target.entryId) && targetBlocker(target) === null); }
export function excludedCounts(state: WizardState): Partial<Record<FirstTouchBlocker, number>> {
  const counts: Partial<Record<FirstTouchBlocker, number>> = {};
  for (const target of state.targets) {
    const blocker = state.selected.includes(target.entryId) ? targetBlocker(target) : null;
    if (blocker) counts[blocker] = (counts[blocker] ?? 0) + 1;
  }
  return counts;
}
/** Selected exclusions in BLOCKER_LABELS order, so the Creators step reads the same every time. */
export function excludedEntries(state: WizardState): Array<[FirstTouchBlocker, number]> {
  const counts = excludedCounts(state);
  return (Object.keys(BLOCKER_LABELS) as FirstTouchBlocker[]).flatMap((blocker) => (counts[blocker] ? [[blocker, counts[blocker]] as [FirstTouchBlocker, number]] : []));
}
/** Creators dropped at the creators step as do-not-contact, counted with approve's suppressed skips. */
export function suppressedDropped(state: WizardState): number { return excludedCounts(state).suppressed ?? 0; }
/** QA (dsa-9Qv, draft 378): exactly the address approve resolves — campaign override, else primary — off the
 *  refetched draft. Never the open-time pane snapshot or the draft's stored to_address. */
export function effectiveRecipient(draft: OutreachDraft): string | undefined {
  return [draft.entry.emailOverride, draft.entry.email].map((candidate) => candidate?.trim()).find((candidate) => !!candidate);
}
export function unverifiedCount(state: WizardState): number { return state.targets.filter((target) => target.unverified && state.selected.includes(target.entryId)).length; }
export function isBatch(state: WizardState): boolean { return sendableTargets(state).length > 1; }
export function scheduledForISO(state: WizardState, campaignZone: string): string | undefined { return state.when === 'later' && state.date && state.time ? zonedDateTimeToISO(state.date, state.time, state.zone ?? campaignZone) : undefined; }
/** The date and time on the wall in `zone` at `at`, in the shapes the date and time inputs take. */
export function zonedNow(zone: string, at: Date = new Date()): { date: string; time: string } {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone: zone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).formatToParts(at).filter((part) => part.type !== 'literal').map((part) => [part.type, part.value]));
  return { date: `${parts.year}-${parts.month}-${parts.day}`, time: `${parts.hour}:${parts.minute}` };
}

/** E9-49: a picked time is only usable while it is still ahead; Go refuses one more than 60 s back. */
export function isFutureSchedule(state: WizardState, campaignZone: string, now: number = Date.now()): boolean {
  const iso = scheduledForISO(state, campaignZone);
  return !!iso && new Date(iso).getTime() > now;
}

/** Go refuses a scheduled send more than 30 days ahead (sending maxScheduleAhead). */
export const MAX_SCHEDULE_AHEAD_MS = 30 * 24 * 60 * 60 * 1000;

/** E9-50/51: the picked wall time, read in the picked zone, is past the 30-day cap. */
export function isBeyondScheduleCap(state: WizardState, zone: string, now: number = Date.now()): boolean {
  const iso = scheduledForISO(state, zone);
  return !!iso && new Date(iso).getTime() > now + MAX_SCHEDULE_AHEAD_MS;
}

export function canContinue(state: WizardState, context: { hasTemplate: boolean; zone?: string | null }): boolean {
  if (state.step === 'creators') return sendableTargets(state).length > 0;
  // Drafting writes from step 1 of the picked template, so it needs a subject and a message there.
  if (state.step === 'template') {
    const first = state.templateSteps?.[0];
    return context.hasTemplate && !!first && first.subject.trim() !== '' && first.body.trim() !== '';
  }
  if (state.step === 'send') {
    if (state.when === 'now') return true;
    if (!state.date || !state.time || isBeyondScheduleCap(state, context.zone ?? 'UTC')) return false;
    return !context.zone || isFutureSchedule(state, context.zone);
  }
  return true;
}
export function sequenceSummary(steps: ReadonlyArray<{ delayDays?: number }>): { count: number; days: number } { return { count: steps.length, days: Math.ceil(steps.reduce((total, step) => total + (step.delayDays ?? 0), 0)) }; }

/** Alias ids (Asia/Calcutta) are the same zone; nothing is a change until the campaign zone is known. */
export function isZoneChange(zone: string | null, campaignZone: string | undefined): boolean {
  return !!zone && !!campaignZone && canonicalId(zone) !== canonicalId(campaignZone);
}


/** Go accepts a batch for 24 h; the margin covers the time between its clock and ours. */
export const WIZARD_BATCH_MAX_AGE_MS = 24 * 60 * 60 * 1000 - 5 * 60 * 1000;

/** The step 3 drafting operation the approve may name, or null when there is none or it is too old for Go. */
export function usableBatchId(run: WizardRun | null | undefined): string | null {
  const batch = run?.batch;
  return batch && Date.now() - batch.startedAt < WIZARD_BATCH_MAX_AGE_MS ? batch.id : null;
}
